import { getI18n } from "react-i18next";
import { showNotification } from "@mantine/notifications";
import { TranslationKeys } from "src/types/i18next";

const MB = 1024 * 1024;

// Assumption A1 (TTV2-1588, product owner's answer pending): 10 MB, measured on the file the
// user picked — never on the cropped PNG the cropper produces from it.
export const MAX_IMAGE_BYTES = 10 * MB;

// Mirrors tt-v2-client-api/lib/lambdas/uploadImage/src/config.ts, which is what the server accepts.
export const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "webp", "svg"];

export const IMAGE_FORMATS_LABEL = IMAGE_EXTENSIONS.map((extension) => extension.toUpperCase()).join(", ");

export type ImageRefusalKey = "image_toolarge" | "image_unsupportedformat";

export interface ImageRefusal {
    // The title key; the explanation is `${key}_message`, filled with `params`.
    key: ImageRefusalKey;
    params: Record<string, string>;
}

const formatMegabytes = (bytes: number, round: (value: number) => number) => {
    const formatter = new Intl.NumberFormat(getI18n()?.language, { maximumFractionDigits: 1 });

    return `${formatter.format(round((bytes / MB) * 10) / 10)} MB`;
};

/**
 * Checks the picked file before anything reads, crops or uploads it. Returns null when it is fine.
 */
export const validateImageFile = (file: File): ImageRefusal | null => {
    const dot = file.name.lastIndexOf(".");
    const extension = dot < 0 ? "" : file.name.slice(dot + 1).toLowerCase();

    if (!IMAGE_EXTENSIONS.includes(extension)) {
        return {
            key: "image_unsupportedformat",
            params: {
                filename: file.name,
                formats: IMAGE_FORMATS_LABEL,
            },
        };
    }

    if (file.size > MAX_IMAGE_BYTES) {
        return {
            key: "image_toolarge",
            params: {
                maxSize: formatMegabytes(MAX_IMAGE_BYTES, Math.round),
                // Rounded up so a file just over the limit never reads "10 MB, limit 10 MB".
                fileSize: formatMegabytes(file.size, Math.ceil),
            },
        };
    }

    return null;
};

const showRefusal = (title: TranslationKeys, message: TranslationKeys, params?: Record<string, string>) => {
    const { t } = getI18n();

    showNotification({
        color: "red",
        title: t(title, { ns: "common" }),
        message: t(message, { ns: "common", ...params }),
        autoClose: 1000 * 10,
    });
};

// A refusal the user can fix by picking another file is not a defect, so it is a toast and not a
// Sentry event (like image_widthtoosmall).
export const showImageRefusal = ({ key, params }: ImageRefusal) => showRefusal(key, `${key}_message`, params);

export const showImageUnreadable = () => showRefusal("error", "image_unreadable");
